import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import ingredientApi from '../../services/ingredientApi';
import Drawer from '../common/Drawer';
import './AddIngredientModal.css';

const ChevronIcon = ({ open }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const FIELD_LABELS = {
  name: 'Name',
  category: 'Category',
  unit: 'Unit',
  cost_per_unit: 'Cost per unit',
  min_stock_level: 'Min stock level',
  max_stock_level: 'Max stock level',
  reorder_point: 'Reorder point',
  lead_time_days: 'Lead time',
  supplier: 'Supplier',
  supplier_sku: 'Supplier SKU',
  shelf_life_days: 'Shelf life',
  specifications: 'Specifications',
  storage_requirements: 'Storage requirements',
  notes: 'Notes',
};

// Fields that live inside the collapsible "Supplier & Notes" section — none
// are currently required, but if that ever changes, an error on one of them
// must never go unseen behind a collapsed section.
const COLLAPSIBLE_FIELDS = ['supplier', 'supplier_sku', 'shelf_life_days', 'specifications', 'storage_requirements', 'notes'];

const AddIngredientModal = ({ isOpen, onClose, onIngredientCreated, ingredient = null }) => {
  const isEditMode = !!ingredient;

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      name: '',
      category: 'COFFEE',
      unit: 'GRAMS',
      cost_per_unit: '',
      supplier: '',
      supplier_sku: '',
      min_stock_level: '',
      max_stock_level: '',
      reorder_point: '',
      lead_time_days: 7,
      specifications: '',
      storage_requirements: '',
      shelf_life_days: '',
      notes: '',
    },
  });

  const [isActive, setIsActive] = useState(true);
  const [isPackagingMaterial, setIsPackagingMaterial] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [supplierOpen, setSupplierOpen] = useState(false);

  const selectedCategory = watch('category');

  useEffect(() => {
    if (!isOpen) return;
    ingredientApi.listCategories().then((cats) =>
      setCategories(cats?.length ? cats : ['COFFEE', 'TEA', 'JUICE', 'FLAVORING', 'ADDITIVES', 'SWEETENER', 'SYRUP', 'OTHER'])
    ).catch(() =>
      setCategories(['COFFEE', 'TEA', 'JUICE', 'FLAVORING', 'ADDITIVES', 'SWEETENER', 'SYRUP', 'OTHER'])
    );
    ingredientApi.listUnits().then((u) =>
      setUnits(u?.length ? u : ['MG', 'KG', 'GRAMS', 'ML', 'LITERS', 'S'])
    ).catch(() =>
      setUnits(['MG', 'KG', 'GRAMS', 'ML', 'LITERS', 'S'])
    );
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (ingredient) {
      reset({
        name:                 ingredient.name || '',
        category:             ingredient.category || 'COFFEE',
        unit:                 ingredient.unit || 'GRAMS',
        cost_per_unit:        ingredient.cost_per_unit ?? '',
        supplier:             ingredient.supplier || '',
        supplier_sku:         ingredient.supplier_sku || '',
        min_stock_level:      ingredient.min_stock_level ?? '',
        max_stock_level:      ingredient.max_stock_level ?? '',
        reorder_point:        ingredient.reorder_point ?? '',
        lead_time_days:       ingredient.lead_time_days ?? 7,
        specifications:       ingredient.specifications || '',
        storage_requirements: ingredient.storage_requirements || '',
        shelf_life_days:      ingredient.shelf_life_days ?? '',
        notes:                ingredient.notes || '',
      });
      setIsActive(ingredient.is_active !== undefined ? ingredient.is_active : true);
      setIsPackagingMaterial(ingredient.is_packaging_material ?? false);
      setSupplierOpen(!!(
        ingredient.supplier || ingredient.supplier_sku ||
        ingredient.specifications || ingredient.storage_requirements ||
        ingredient.shelf_life_days || ingredient.notes
      ));
    } else {
      reset({
        name: '', category: 'COFFEE', unit: 'GRAMS', cost_per_unit: '',
        supplier: '', supplier_sku: '', min_stock_level: '', max_stock_level: '',
        reorder_point: '', lead_time_days: 7, specifications: '',
        storage_requirements: '', shelf_life_days: '', notes: '',
      });
      setIsActive(true);
      setIsPackagingMaterial(false);
      setSupplierOpen(false);
    }
    setFieldErrors({});
  }, [isOpen, ingredient, reset]);

  // If a submit attempt leaves an error on a field inside the collapsed
  // "Supplier & Notes" section, force it open — a required field must never
  // be effectively invisible just because its section is collapsed.
  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.some((key) => COLLAPSIBLE_FIELDS.includes(key))) {
      setSupplierOpen(true);
    }
  }, [errors]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setFieldErrors({});
    try {
      const payload = { ...data, is_active: isActive, is_packaging_material: isPackagingMaterial };
      const result = isEditMode
        ? await ingredientApi.update(ingredient.id, payload)
        : await ingredientApi.create(payload);
      onIngredientCreated(result);
      onClose();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFieldErrors(error.response.data.errors);
      } else {
        setFieldErrors({
          general: error.response?.data?.detail || error.response?.data?.message ||
            `Unable to ${isEditMode ? 'update' : 'create'} ingredient — please try again.`,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const err = (field) => errors[field]?.message || fieldErrors[field];

  const footer = (
    <>
      <button type="button" className="app-drawer-btn app-drawer-btn--secondary" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </button>
      <button type="button" className="app-drawer-btn app-drawer-btn--primary" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
        {isSubmitting
          ? (isEditMode ? 'Saving…' : 'Creating…')
          : (isEditMode ? 'Save Changes' : 'Add Ingredient')}
      </button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Ingredient' : 'New Ingredient'}
      footer={footer}
    >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            {fieldErrors.general && (
              <div className="aim-error" role="alert">{fieldErrors.general}</div>
            )}

            {Object.keys(errors).length > 0 && (
              <div className="aim-error" role="alert">
                Please fix the highlighted field{Object.keys(errors).length === 1 ? '' : 's'} before continuing: {' '}
                {Object.keys(errors).map((key) => FIELD_LABELS[key] || key).join(', ')}
              </div>
            )}

            {/* ── Identity ── */}
            <div className="aim-section">
              {/* Name + toggle */}
              <div className="aim-identity-row">
                <div className="aim-field aim-field-grow">
                  <label className="aim-label" htmlFor="name">
                    Name <span className="aim-required">*</span>
                  </label>
                  <input
                    id="name"
                    autoFocus
                    className={`aim-input ${err('name') ? 'aim-input-error' : ''}`}
                    placeholder="e.g. Arabica Coffee Beans"
                    {...register('name', {
                      required: 'Name is required',
                      maxLength: { value: 255, message: 'Max 255 characters' },
                    })}
                  />
                  {err('name') && <span className="aim-field-error">{err('name')}</span>}
                </div>

                <div className="aim-field">
                  <label className="aim-label">Status</label>
                  <button
                    type="button"
                    className={`aim-toggle ${isActive ? 'on' : 'off'}`}
                    onClick={() => setIsActive((v) => !v)}
                    aria-pressed={isActive}
                  >
                    <span className="aim-toggle-track">
                      <span className="aim-toggle-knob" />
                    </span>
                    <span className="aim-toggle-text">{isActive ? 'Active' : 'Inactive'}</span>
                  </button>
                </div>

                <div className="aim-field">
                  <label className="aim-label">Type</label>
                  <button
                    type="button"
                    className={`aim-toggle ${isPackagingMaterial ? 'on' : 'off'} aim-toggle-packaging`}
                    onClick={() => setIsPackagingMaterial((v) => !v)}
                    aria-pressed={isPackagingMaterial}
                    title="Packaging materials (cans, labels) are tracked in inventory but excluded from recipes"
                  >
                    <span className="aim-toggle-track">
                      <span className="aim-toggle-knob" />
                    </span>
                    <span className="aim-toggle-text">{isPackagingMaterial ? 'Packaging' : 'Ingredient'}</span>
                  </button>
                </div>
              </div>

              {/* Category + Unit */}
              <div className="aim-two-col">
                <div className="aim-field">
                  <label className="aim-label" htmlFor="category">
                    Category <span className="aim-required">*</span>
                  </label>
                  <select
                    id="category"
                    className={`aim-select ${err('category') ? 'aim-input-error' : ''}`}
                    {...register('category', { required: 'Category is required' })}
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {err('category') && <span className="aim-field-error">{err('category')}</span>}
                </div>

                <div className="aim-field">
                  <label className="aim-label" htmlFor="unit">
                    Unit {selectedCategory !== 'OTHER' && <span className="aim-required">*</span>}
                  </label>
                  <select
                    id="unit"
                    className={`aim-select ${err('unit') ? 'aim-input-error' : ''}`}
                    {...register('unit', {
                      required: selectedCategory !== 'OTHER' ? 'Unit is required' : false,
                    })}
                  >
                    <option value="">Select unit</option>
                    {units.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {err('unit') && <span className="aim-field-error">{err('unit')}</span>}
                </div>
              </div>

              {/* Cost per unit */}
              <div className="aim-field">
                <label className="aim-label" htmlFor="cost_per_unit">Cost per unit</label>
                <div className="aim-prefix-wrap">
                  <span className="aim-prefix">$</span>
                  <input
                    id="cost_per_unit"
                    type="number" step="0.0001" min="0"
                    className={`aim-input aim-input-prefixed ${err('cost_per_unit') ? 'aim-input-error' : ''}`}
                    placeholder="0.00"
                    {...register('cost_per_unit', {
                      min: { value: 0, message: 'Must be ≥ 0' },
                      valueAsNumber: true,
                    })}
                  />
                </div>
                {err('cost_per_unit') && <span className="aim-field-error">{err('cost_per_unit')}</span>}
              </div>
            </div>

            {/* ── Stock Levels ── */}
            <div className="aim-section">
              <p className="aim-section-label">Stock Levels</p>

              {isEditMode && ingredient?.current_stock != null && (
                <div className="aim-stock-banner">
                  <span className="aim-stock-label">Current stock on hand</span>
                  <span className="aim-stock-value">{ingredient.current_stock} {ingredient.unit || ''}</span>
                </div>
              )}

              {/* Min → Reorder → Max range */}
              <div className="aim-stock-range">
                <div className="aim-field">
                  <label className="aim-label" htmlFor="min_stock_level">
                    Min <span className="aim-required">*</span>
                    <span className="aim-field-hint">Alert threshold</span>
                  </label>
                  <input
                    id="min_stock_level" type="number" min="0" step="1"
                    className={`aim-input aim-input-center ${err('min_stock_level') ? 'aim-input-error' : ''}`}
                    placeholder="0"
                    {...register('min_stock_level', {
                      required: 'Required',
                      min: { value: 0, message: 'Must be ≥ 0' },
                      valueAsNumber: true,
                    })}
                  />
                  {err('min_stock_level') && <span className="aim-field-error">{err('min_stock_level')}</span>}
                </div>

                <div className="aim-range-arrow" aria-hidden="true">→</div>

                <div className="aim-field">
                  <label className="aim-label" htmlFor="reorder_point">
                    Reorder
                    <span className="aim-field-hint">Trigger reorder</span>
                  </label>
                  <input
                    id="reorder_point" type="number" min="0" step="1"
                    className={`aim-input aim-input-center ${err('reorder_point') ? 'aim-input-error' : ''}`}
                    placeholder="0"
                    {...register('reorder_point', {
                      min: { value: 0, message: 'Must be ≥ 0' },
                      valueAsNumber: true,
                    })}
                  />
                  {err('reorder_point') && <span className="aim-field-error">{err('reorder_point')}</span>}
                </div>

                <div className="aim-range-arrow" aria-hidden="true">→</div>

                <div className="aim-field">
                  <label className="aim-label" htmlFor="max_stock_level">
                    Max
                    <span className="aim-field-hint">Upper limit</span>
                  </label>
                  <input
                    id="max_stock_level" type="number" min="0" step="1"
                    className={`aim-input aim-input-center ${err('max_stock_level') ? 'aim-input-error' : ''}`}
                    placeholder="0"
                    {...register('max_stock_level', {
                      min: { value: 0, message: 'Must be ≥ 0' },
                      valueAsNumber: true,
                    })}
                  />
                  {err('max_stock_level') && <span className="aim-field-error">{err('max_stock_level')}</span>}
                </div>
              </div>

              {/* Lead time */}
              <div className="aim-field">
                <label className="aim-label" htmlFor="lead_time_days">Lead Time</label>
                <div className="aim-suffix-wrap">
                  <input
                    id="lead_time_days" type="number" min="0" step="1"
                    className={`aim-input aim-input-suffixed ${err('lead_time_days') ? 'aim-input-error' : ''}`}
                    placeholder="7"
                    {...register('lead_time_days', {
                      min: { value: 0, message: 'Must be ≥ 0' },
                      valueAsNumber: true,
                    })}
                  />
                  <span className="aim-suffix">days</span>
                </div>
              </div>
            </div>

            {/* ── Supplier & Notes (collapsible) ── */}
            <div className="aim-section aim-section-last">
              <button
                type="button"
                className={`aim-collapse-toggle ${supplierOpen ? 'open' : ''}`}
                onClick={() => setSupplierOpen((v) => !v)}
                aria-expanded={supplierOpen}
              >
                <span>Supplier &amp; Notes</span>
                <span className="aim-collapse-right">
                  {!supplierOpen && <span className="aim-collapse-hint">optional</span>}
                  <ChevronIcon open={supplierOpen} />
                </span>
              </button>

              {supplierOpen && (
                <div className="aim-collapsible">
                  <div className="aim-two-col">
                    <div className="aim-field">
                      <label className="aim-label" htmlFor="supplier">Supplier</label>
                      <input
                        id="supplier" type="text"
                        className="aim-input"
                        placeholder="e.g. Orinoco Coffee Co."
                        {...register('supplier')}
                      />
                    </div>
                    <div className="aim-field">
                      <label className="aim-label" htmlFor="supplier_sku">Supplier SKU</label>
                      <input
                        id="supplier_sku" type="text"
                        className="aim-input"
                        placeholder="e.g. ARB-001"
                        {...register('supplier_sku')}
                      />
                    </div>
                  </div>

                  <div className="aim-field">
                    <label className="aim-label" htmlFor="shelf_life_days">Shelf Life</label>
                    <div className="aim-suffix-wrap">
                      <input
                        id="shelf_life_days" type="number" min="0" step="1"
                        className="aim-input aim-input-suffixed"
                        placeholder="0"
                        {...register('shelf_life_days', {
                          min: { value: 0, message: 'Must be ≥ 0' },
                          valueAsNumber: true,
                        })}
                      />
                      <span className="aim-suffix">days</span>
                    </div>
                  </div>

                  <div className="aim-field">
                    <label className="aim-label" htmlFor="specifications">Specifications</label>
                    <textarea
                      id="specifications" rows="2"
                      className="aim-textarea"
                      placeholder="Quality or technical specifications"
                      {...register('specifications')}
                    />
                  </div>

                  <div className="aim-field">
                    <label className="aim-label" htmlFor="storage_requirements">Storage Requirements</label>
                    <textarea
                      id="storage_requirements" rows="2"
                      className="aim-textarea"
                      placeholder="e.g. Keep refrigerated below 4°C"
                      {...register('storage_requirements')}
                    />
                  </div>

                  <div className="aim-field">
                    <label className="aim-label" htmlFor="notes">Notes</label>
                    <textarea
                      id="notes" rows="2"
                      className="aim-textarea"
                      placeholder="Any additional internal notes"
                      {...register('notes')}
                    />
                  </div>
                </div>
              )}
            </div>

          </form>
    </Drawer>
  );
};

export default AddIngredientModal;
